import React, { useState, useCallback, useId } from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff } from "lucide-react";
import VoiceRecorder from "./voiceRecorder";
import { toast } from "@/components/ui/sonner";
import { TooltipWrapper } from '@/components/ui/tooltip-wrapper';
import { logger } from "@/lib/logger";

interface VoiceInputButtonProps {
  onTranscription: (text: string) => void;
  isDisabled?: boolean;
  variant?: "default" | "ghost" | "outline" | "destructive";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  /** Called when recording starts (e.g. to capture base content for streaming) */
  onRecordingStart?: () => void;
  /** Called when recording stops */
  onRecordingStop?: () => void;
  /** Live transcript as words stream in (final + interim) */
  onStreamingTranscript?: (text: string) => void;
}

const VoiceInputButton = ({ 
  onTranscription, 
  isDisabled = false,
  variant,
  size = "icon",
  className = "",
  onRecordingStart,
  onRecordingStop,
  onStreamingTranscript
}: VoiceInputButtonProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [voiceRecorder, setVoiceRecorder] = useState<VoiceRecorder | null>(null);
  const [isSupportedBrowser, setIsSupportedBrowser] = useState<boolean | null>(null);
  const tooltipId = useId();
  
  // Check for browser support on component mount
  React.useEffect(() => {
    const checkBrowserSupport = () => {
      logger.debug("Checking browser support for Web Speech API");

      const speechRecognition = window.SpeechRecognition;
      const webkitSpeechRecognition = window.webkitSpeechRecognition;
      const mozSpeechRecognition = (window as unknown as { mozSpeechRecognition?: unknown }).mozSpeechRecognition;
      const msSpeechRecognition = (window as unknown as { msSpeechRecognition?: unknown }).msSpeechRecognition;

      logger.debug("Browser API availability", {
        SpeechRecognition: !!speechRecognition,
        webkitSpeechRecognition: !!webkitSpeechRecognition,
        mozSpeechRecognition: !!mozSpeechRecognition,
        msSpeechRecognition: !!msSpeechRecognition,
        isHTTPS: window.location.protocol === 'https:',
        userAgent: navigator.userAgent
      });

      const isSupported = !!(speechRecognition ??
        webkitSpeechRecognition ??
        mozSpeechRecognition ??
        msSpeechRecognition);

      logger.info(`Browser support result: ${isSupported ? 'Supported' : 'Not supported'}`);
      setIsSupportedBrowser(isSupported);
      return isSupported;
    };

    checkBrowserSupport();
  }, []);
  
  const handleRecordingStart = useCallback(() => {
    setIsRecording(true);
    onRecordingStart?.();
  }, [onRecordingStart]);
  
  const handleRecordingStop = useCallback(() => {
    setIsRecording(false);
    onRecordingStop?.();
  }, [onRecordingStop]);
  
  const handleError = useCallback((error: Error) => {
    logger.error("Voice recording error", { error: error.message, stack: error.stack });
    setIsRecording(false);
  }, []);
  
  const toggleRecording = useCallback(() => {
    logger.debug("Voice button clicked", { isDisabled, isRecording, isSupportedBrowser });

    if (isDisabled) {
      logger.debug("Voice recording disabled");
      return;
    }

    if (isRecording && voiceRecorder) {
      logger.debug("Stopping recording");
      voiceRecorder.stopRecording();
      return;
    }

    // Check browser support
    if (isSupportedBrowser === false) {
      logger.warn("Browser not supported for voice input");
      toast.error("Your browser doesn't support voice recognition. Try Chrome or Edge.");
      return;
    }

    if (isSupportedBrowser === null) {
      logger.debug("Browser support still being checked");
      toast.info("Checking browser support...");
      return;
    }

    logger.info("Starting new voice recording");
    const recorder = new VoiceRecorder({
      onTranscription,
      onRecordingStart: handleRecordingStart,
      onRecordingStop: handleRecordingStop,
      onError: handleError,
      onStreamingTranscript,
    });

    setVoiceRecorder(recorder);
    recorder.startRecording();
  }, [isRecording, isDisabled, voiceRecorder, isSupportedBrowser, onTranscription, onStreamingTranscript, handleRecordingStart, handleRecordingStop, handleError]);
  
  // If browser support is still being checked or already determined as not supported, show appropriate button state
  const buttonVariant = isRecording ? "destructive" : (variant ?? "ghost");
  const tooltipText = isSupportedBrowser === false 
    ? "Voice input not supported in this browser" 
    : (isRecording ? "Stop recording" : "Start voice input");
  
  return (
    <>
      <TooltipWrapper
        content={tooltipText}
        side="top"
        align="center"
        sideOffset={14}
        delayDuration={400}
        priority="high"
        className="opacity-70 text-xs"
      >
        <div className="relative z-[var(--z-tooltip,200)]">
          <Button
            type="button"
            size={size}
            variant={buttonVariant}
            onClick={toggleRecording}
            disabled={isDisabled || isSupportedBrowser === false}
            className={`relative ${
              (isDisabled || isSupportedBrowser === false) 
                ? 'cursor-not-allowed' 
                : 'cursor-pointer hover:scale-105 transition-transform duration-150'
            } ${className}`}
            aria-label={tooltipText}
            aria-describedby={tooltipId}
          >
            {isRecording ? (
              <MicOff className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
            {isRecording && (
              <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            )}
          </Button>
        </div>
      </TooltipWrapper>
      <span id={tooltipId} className="sr-only">{tooltipText}</span>
    </>
  );
};

export default VoiceInputButton;
